<script setup lang="ts">
  import { computed, useId } from 'vue';

  import { ColoredButton, IconImage } from '@/shared/ui';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';

  import type { LocalUrl } from '../model/electronic-attachments';

  const props = defineProps<{
    localUrls: LocalUrl[];
    maxAttachments: number;
    totalAttachments: number;
    urlErrors: string[];
    limitMessage: string;
  }>();

  const emit = defineEmits<{
    (event: 'addUrl'): void;
    (event: 'removeUrl', index: number): void;
    (event: 'updateUrl', index: number, value: string): void;
    (event: 'blurUrl', index: number): void;
  }>();

  const sectionId = useId();

  const isAtLimit = computed(() => props.totalAttachments >= props.maxAttachments);
  const isOverLimit = computed(() => props.totalAttachments > props.maxAttachments);

  const titleId = `${sectionId}-title`;
  const limitMessageId = `${sectionId}-limit`;

  const urlInputId = (index: number) => `${sectionId}-url-${index}`;
  const urlErrorId = (index: number) => `${sectionId}-url-${index}-error`;

  const updateUrl = (index: number, event: Event) => {
    emit('updateUrl', index, (event.target as HTMLInputElement).value);
  };
</script>

<template>
  <section
    class="flex flex-col gap-4"
    :aria-labelledby="titleId"
    data-testid="electronic-url-section"
  >
    <h3 :id="titleId" class="text-sm font-semibold text-gray-07" data-testid="url-section-title">
      Adicionar documento via URL:
    </h3>

    <p v-if="localUrls.length === 0" class="text-xs text-gray-03" data-testid="url-empty-state">
      Nenhuma URL adicionada. Use "Adicionar URL" para incluir links de documentos.
    </p>

    <div v-else class="flex flex-col gap-3" data-testid="url-list">
      <div
        v-for="(urlItem, index) in localUrls"
        :key="urlItem.rowId ?? index"
        class="flex flex-col gap-2"
        data-testid="url-list-item"
      >
        <div class="flex items-center gap-3">
          <div class="relative flex-1">
            <label :for="urlInputId(index)" class="sr-only">URL {{ index + 1 }}</label>
            <input
              :id="urlInputId(index)"
              :value="urlItem.url"
              type="text"
              inputmode="url"
              placeholder="Cole aqui a URL do documento"
              class="w-full rounded-lg border bg-white-01 py-3 pr-4 pl-10 text-sm text-gray-07 focus:outline-none"
              :class="urlErrors[index] ? 'border-red-16' : 'border-gray-02'"
              :aria-invalid="Boolean(urlErrors[index])"
              :aria-describedby="urlErrors[index] ? urlErrorId(index) : undefined"
              data-testid="url-value-input"
              @input="updateUrl(index, $event)"
              @blur="emit('blurUrl', index)"
            />
            <IconImage
              :src="LinkIcon"
              alt="URL"
              class="absolute top-1/2 left-3 -translate-y-1/2 text-gray-03"
            />
          </div>

          <button
            type="button"
            class="cursor-pointer text-gray-03 hover:text-red-16"
            aria-label="Remover URL"
            data-testid="remove-url-button"
            @click="emit('removeUrl', index)"
          >
            <IconImage :src="TrashIcon" alt="Remover URL" />
          </button>
        </div>

        <p
          v-if="urlErrors[index]"
          :id="urlErrorId(index)"
          class="text-xs text-red-16"
          role="alert"
          data-testid="url-error-message"
        >
          {{ urlErrors[index] }}
        </p>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3">
      <p
        v-if="isAtLimit"
        :id="limitMessageId"
        class="text-xs"
        :class="isOverLimit ? 'text-red-16' : 'text-gray-03'"
        role="status"
        aria-live="polite"
        data-testid="url-limit-message"
      >
        {{ limitMessage }}
      </p>
      <ColoredButton
        :icon="PlusIcon"
        icon-alt="Adicionar"
        text="Adicionar URL"
        :disabled="isAtLimit"
        :aria-describedby="isAtLimit ? limitMessageId : undefined"
        data-testid="add-url-button"
        @mousedown.prevent
        @click="emit('addUrl')"
      />
    </div>
  </section>
</template>
